import React, { useRef, useEffect, useCallback } from 'react';

import { useSelector } from 'react-redux';
import {
  Trans,
  useBi,
  useTranslation,
  useEnvironment,
} from '@wix/yoshi-flow-editor';
import { groupCreatePostClick } from '@wix/bi-logger-groups/v2';
import { useCurrentStateAndParams, useRouter } from '@wix/tpa-router/react';

import { GroupAppKey } from 'store/groups/types';
import {
  selectGroupIdBySlug,
  selectIsAppAvailable,
  selectMediaItems,
} from 'store/selectors';

import { useDidUpdate } from 'common/hooks';
import { useController } from 'common/context/controller';

import { EmptyState } from 'wui/EmptyState';
import { ErrorState } from 'wui/ErrorState';
import { Spinner } from 'wui/Spinner';
import { Button } from 'wui/Button';

import { MediaGallery } from './Gallery';
import { WIDGET_MEDIA_EMPTY_STATE, WIDGET_MEDIA_LOAD_MORE_ERROR_STATE, WIDGET_MEDIA_LOAD_MORE_LOADING_STATE } from './dataHooks';
import { RestrictedState } from '../RestrictedState';

export function MediaPage() {
  const { t } = useTranslation();
  const router = useRouter();
  const { isMobile } = useEnvironment();
  const { params } = useCurrentStateAndParams();
  const bi = useBi();

  const { group$, media$ } = useController();
  const { items, loading, loadingMore, error, nextOffset, loadMoreError } =
    useSelector(selectMediaItems);
  const groupId = useSelector(selectGroupIdBySlug(params.slug));

  const isAvailable = useSelector(
    selectIsAppAvailable({
      groupId,
      application: GroupAppKey.GALLERY_APP,
    }),
  );

  const lastRequestedOffsetRef = useRef<number | null>(null);
  const fetchInFlightRef = useRef(false);
  const hasMore = typeof nextOffset === 'number';


  useEffect(() => {
    // reset when feed context changes
    lastRequestedOffsetRef.current = null;
    fetchInFlightRef.current = false;
  }, [groupId]);

  useEffect(() => {
    if (!loadingMore) {
      fetchInFlightRef.current = false;
    }
  }, [loadingMore]);

  useEffect(() => {
    // release dedupe lock when cursor changed
    if (lastRequestedOffsetRef.current !== nextOffset) {
      fetchInFlightRef.current = false;
    }
  }, [nextOffset]);


  const handleFetchMore = useCallback(() => {

    if (
      !groupId ||
      typeof nextOffset !== 'number' ||
      loadingMore ||
      loadMoreError ||
      fetchInFlightRef.current ||
      lastRequestedOffsetRef.current === nextOffset
    ) {
      return;
    }

    lastRequestedOffsetRef.current = nextOffset;
    fetchInFlightRef.current = true;
    void media$.fetch(groupId, isMobile, nextOffset);
  }, [groupId, isMobile, nextOffset, loadingMore, loadMoreError, media$]);


  useDidUpdate(() => {
    if (isAvailable) {
      media$.fetch(groupId, isMobile);
    }
  }, [isAvailable]);

  if (loading) {
    return <EmptyState title={<Spinner />} />;
  }

  if (!isAvailable) {
    return <RestrictedState application={GroupAppKey.GALLERY_APP} />;
  }

  if (error) {
    return (
      <ErrorState
        wired
        title={t('groups-web.discussion.error-state.title')}
        subtitle={
          <Trans
            i18nKey={t('groups-web.discussion.error-state.owner.message')}
            components={[<span key={0} />, <br key={1} />, <span key={2} />]}
          />
        }
        actionLabel={t('groups-web.discussion.error-state.cta')}
        onRetry={handleRetry}
      />
    );
  }

  if (!items.length) {
    return (
      <EmptyState
        wired
        data-hook={WIDGET_MEDIA_EMPTY_STATE}
        title={t('groups-web.media.empty.title')}
        subtitle={t('groups-web.media.empty.description')}
        action={
          <Button onClick={onCreatePost}>
            {t('groups-web.discussion.first.button')}
          </Button>
        }
      />
    );
  }

  return (
    <>
      <MediaGallery items={items} fetchMore={handleFetchMore} />
      {loadingMore && (
        <EmptyState
          data-hook={WIDGET_MEDIA_LOAD_MORE_LOADING_STATE}
          title={<Spinner />}
          variant="section"
        />
      )}
      {loadMoreError && hasMore && (
        <ErrorState
          data-hook={WIDGET_MEDIA_LOAD_MORE_ERROR_STATE}
          wired
          title={t('groups-web.discussion.error-state.title')}
          subtitle={
            <Trans
              i18nKey={t('groups-web.discussion.error-state.owner.message')}
              components={[<span key={0} />, <br key={1} />, <span key={2} />]}
            />
          }
          actionLabel={t('groups-web.discussion.error-state.cta')}
          onRetry={handleRetryLoadMore}
          variant="section"
        />
      )}
    </>
  );
  function handleRetry() {
    media$.fetch(groupId, isMobile);
  }

  function handleRetryLoadMore() {
    if (!groupId || typeof nextOffset !== 'number' || loadingMore) {
      return;
    }

    // explicit user retry for same failed offset
    lastRequestedOffsetRef.current = nextOffset;
    fetchInFlightRef.current = true;
    void media$.fetch(groupId, isMobile, nextOffset);
  }

  function onCreatePost() {
    bi.report(
      groupCreatePostClick({
        groupId,
        origin: 'media_tab_btn',
      }),
    );

    router.go('group.discussion.feed');
    group$.openCreatePostDialog(groupId);
  }
}

MediaPage.displayName = 'MediaPage';
